// ─────────────────────────────────────────────────────────────────────────────
// callTarget.ts — WHAT THE CALL BUTTON ON *THIS* AGENT'S PAGE MAY DO.
//
// Pure. No React, no fetch, no roster. It takes the agent whose page it is on
// and the ONE authoritative fact about the box, and returns what the surface may
// say and do. Pure because the interesting cases are states, not renders — and a
// state machine that can only be exercised through a component is a state
// machine that gets exercised in three of its five states.
//
// ── THE AUTHORITY IS `boxCall`, NEVER LOCAL STATE ──────────────────────────
// `VoiceCall` carries `domain` and `agent`, and the wire says of them:
// "AUTHORITATIVE — the pair the box actually has, never the one requested."
// So the orb and every per-agent button read the SAME fact and cannot disagree:
// if a call with BUCK is live, DELTA's button says so, because both are reading
// the box rather than each keeping an opinion. That is the `has_setting` rule
// again — two surfaces, one answer.
//
// ── REFUSING IS THE POINT, NOT A MISSING FEATURE (owner ruling 2026-08-27) ──
// `start()` at daemon.py:2716-2724 takes the session lock, CLEARS `_held_call`,
// reassigns the binding and bumps `session_gen`. So a button on DELTA pressed
// during a live BUCK call would not switch — it would DISCARD THE HELD CALL, and
// the owner would not know he had done it. This returns `busy_elsewhere` and
// NAMES the other end. A dead button is not an answer; "a call with BUCK is in
// progress" is.
//
// The agent-to-agent handover exists in the daemon and is VOICE-DRIVEN. It is
// not re-triggered from here — that is not a client decision.
// ─────────────────────────────────────────────────────────────────────────────

/** The box's call, as the wire defines it. Structural — not imported, so this
 *  module stays free of the daemon and its transport. */
export interface CallTargetBoxCall {
  call_id: string;
  domain: string;
  agent: string;
  since?: number;
}

export type CallTargetKind =
  /** No call anywhere. This button may start one. */
  | 'idle'
  /** This button started one and the box has not yet confirmed it. */
  | 'connecting'
  /** The live call is with THIS agent. The button ends it. */
  | 'in_call'
  /** The live call is with ANOTHER agent. THIS BUTTON REFUSES, and names them. */
  | 'busy_elsewhere'
  /** The lane cannot take a call. The button says so instead of failing on press. */
  | 'unavailable';

export interface CallTargetInput {
  /** The agent whose page this is. Not the active agent, not a default. */
  domain: string;
  agent: string | null;
  /** The ONE authoritative fact. */
  boxCall: CallTargetBoxCall | null;
  /** True while this surface's own start is in flight. */
  starting?: boolean;
  /** The call was already running when this window arrived. */
  adopted?: { since: number | null } | null;
  /** False when the voice lane is known to be unreachable. Undefined = unknown,
   *  which is NOT the same as down and must not render as down. */
  laneReady?: boolean;
  /** A transport/lane error the surface should show verbatim-ish. */
  error?: string | null;
}

export interface CallTargetVerdict {
  kind: CallTargetKind;
  /** Uppercase display name of the agent this button belongs to. */
  name: string;
  /** Present ONLY for `busy_elsewhere` — who the live call is actually with. */
  otherAgent: string | null;
  /** May the button be pressed at all. */
  actionable: boolean;
  /** What pressing it would do. `null` when nothing may be pressed. */
  action: 'start' | 'end' | null;
  /** True when the live call predates this window (`in_call` only). */
  adopted: boolean;
  reason: string | null;
}

const up = (v: unknown) => String(v ?? '').trim().toUpperCase();

/** Thrown by `assertCallAllowed` when the box already holds someone else's call. */
export class CallRefused extends Error {
  readonly otherAgent: string;
  constructor(otherAgent: string) {
    super(`a call with ${otherAgent} is in progress`);
    this.name = 'CallRefused';
    this.otherAgent = otherAgent;
  }
}

/**
 * THE WIRE GUARD. Refuses a start that would land on another agent's live call.
 *
 * IT EXISTS BECAUSE THE COMPONENT'S REFUSAL IS NOT ENOUGH. A button that renders
 * `busy_elsewhere` never calls start — but nothing stopped ANY OTHER caller of
 * the context's `start` from doing it, and `start()` clears `_held_call` and
 * reassigns the binding. The mutation proof found this: the "second guard" this
 * component thought it had was a press-time re-check that CANNOT FIRE, because a
 * context change re-renders and the render verdict is already current. That is
 * one guard wearing two hats. This is the second one, and it sits at the call
 * layer where every caller passes.
 *
 * ONE DEFINITION: it asks `resolveCallTarget`, the same function the surface
 * renders from, so the guard and the label can never disagree.
 */
export function assertCallAllowed(
  boxCall: CallTargetBoxCall | null, domain: string, agent: string,
): void {
  const v = resolveCallTarget({ domain, agent, boxCall });
  if (v.kind === 'busy_elsewhere') throw new CallRefused(String(v.otherAgent));
}

/**
 * The verdict. NEVER throws, and never invents a call: an absent or malformed
 * `boxCall` is NO CALL, which is the direction the whole mechanism fails in —
 * the same fail-closed choice the loop toggle makes toward OFF.
 */
export function resolveCallTarget(input: CallTargetInput): CallTargetVerdict {
  const name = up(input.agent);
  const domain = up(input.domain);

  const base: CallTargetVerdict = {
    kind: 'unavailable', name, otherAgent: null,
    actionable: false, action: null, adopted: false, reason: null,
  };

  // No agent is not a call target. CEO/null reaches here from a shared surface.
  if (!name || !domain) {
    return { ...base, reason: 'no agent on this surface' };
  }
  // Explicitly false, not merely absent. `undefined` means we have not read the
  // lane yet, and "unknown" must never render as "down".
  if (input.laneReady === false) {
    return { ...base, reason: input.error || 'the voice lane is not reachable' };
  }

  // A call object without an identity cannot be attributed to anyone, so it is
  // not treated as a call with this agent — it is not treated as a call at all.
  const call = input.boxCall && up(input.boxCall.agent) && up(input.boxCall.domain)
    ? input.boxCall : null;

  if (call) {
    const same = up(call.agent) === name && up(call.domain) === domain;
    if (same) {
      return { ...base, kind: 'in_call', actionable: true, action: 'end',
               adopted: !!input.adopted,
               reason: input.adopted ? 'this call was already running when you arrived' : null };
    }
    // REFUSE, AND NAME THE OTHER END.
    return { ...base, kind: 'busy_elsewhere', otherAgent: up(call.agent),
             actionable: false, action: null,
             reason: `a call with ${up(call.agent)} is in progress` };
  }

  // No box call. A start of our own may still be in flight — and ending while
  // connecting IS the cancel, which is the daemon's own rule, so it stays
  // actionable rather than freezing the control.
  if (input.starting) {
    return { ...base, kind: 'connecting', actionable: true, action: 'end',
             reason: 'connecting — press again to cancel' };
  }
  return { ...base, kind: 'idle', actionable: true, action: 'start', reason: null };
}
